/**
 * M3X.WORKSPACE F.3 — Rail 2 Thin state hover-peek popout.
 *
 * M3X.WORKSPACE-CLOSURE Session 2.7 PR 3.B — restyled per the
 * closure-hotfix spec table:
 *   - 220px width, 10px radius, soft shadow, padding 8px
 *   - Title: uppercase 10px #94a3b8 letter-spaced, "Sub-domain · N screens"
 *   - Screen items: 11px font, 6px 10px padding, 6px radius
 *   - Active screen: linear-gradient(90deg, #eef2ff, #ecfeff), #2260C9 text, 600 weight
 *   - Hover screen: bg #f1f5f9, color #1e293b
 *   - Arrow indicator: 10×10 white triangle pointing back at the icon
 *   - Fade-in 150ms / fade-out 100ms (CSS transition handled by parent unmount)
 */

import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

import type { SubDomain } from "../navigation/registry";


export interface Rail2PopoutProps {
  subDomain: SubDomain;
  locale?: "en" | "ar";
  currentPath: string;
  /** Top offset (px) — sets the popout vertically aligned to the icon. */
  topPx: number;
  onClose: () => void;
  /** Click in popout navigates AND re-expands Rail 2 (closure-hotfix §3.B). */
  onScreenClick?: () => void;
  /**
   * M3X.WORKSPACE-CLOSURE Session 2.8 PR 2 — cursor-bridging callback.
   * When the user moves from the icon to the popout, the parent's
   * close timer must cancel so the popout doesn't close. The parent
   * Rail2 hands its `_cancelClose` ref so onMouseEnter on the popout
   * can call it.
   */
  onMouseEnterPopout?: () => void;
  /**
   * M3X.WORKSPACE-CLOSURE FINAL PR 4 — per-icon positioning mode.
   * When true, the popout uses `insetInlineStart: 100%` relative to
   * its parent (a per-icon wrapper div) so it docks immediately
   * adjacent to whichever icon is hovered. When false (legacy),
   * the popout positions at fixed `insetInlineStart: 118px` from
   * the rail's start — used to anchor to the rail container.
   */
  perIconAdjacent?: boolean;
}


export function Rail2Popout({
  subDomain,
  locale = "en",
  currentPath,
  topPx,
  onClose,
  onScreenClick,
  onMouseEnterPopout,
  perIconAdjacent = false,
}: Rail2PopoutProps) {
  const ref = useRef<HTMLDivElement>(null);
  // M3X.WORKSPACE-CLOSURE Session 2.8 PR 2.B — shift up if the popout
  // would bleed below the viewport fold. Measured after mount.
  const [adjustedTopPx, setAdjustedTopPx] = useState<number>(topPx);

  // Esc closes
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  // PR 2.B — viewport-fit measurement after mount
  useEffect(() => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    if (rect.bottom > window.innerHeight - 16) {
      const overflow = rect.bottom - (window.innerHeight - 16);
      setAdjustedTopPx(topPx - overflow);
    } else {
      setAdjustedTopPx(topPx);
    }
  }, [topPx, subDomain.id]);

  const subDomainLabel = locale === "ar" ? subDomain.labelAr : subDomain.labelEn;
  const screenCount = subDomain.screens.length;

  return (
    <div
      ref={ref}
      data-popout-sub-domain={subDomain.id}
      role="dialog"
      aria-label={subDomainLabel}
      style={
        perIconAdjacent
          ? {
              // M3X.WORKSPACE-CLOSURE FINAL PR 4 — per-icon positioning:
              // 100% of the wrapper width (= icon container's 40px) +
              // 4px gap. Popout docks IMMEDIATELY adjacent to the
              // hovered icon, never far across the content area.
              top: 0,
              insetInlineStart: "100%",
              marginInlineStart: "4px",
              width: "220px",
              backgroundColor: "white",
              border: "1px solid #e2e8f0",
              borderRadius: "10px",
              boxShadow: "0 10px 30px rgba(0,0,0,0.15)",
              padding: "8px",
              zIndex: 50,
            }
          : {
              top: adjustedTopPx,
              insetInlineStart: 118,
              width: "220px",
              backgroundColor: "white",
              border: "1px solid #e2e8f0",
              borderRadius: "10px",
              boxShadow: "0 10px 30px rgba(0,0,0,0.15)",
              padding: "8px",
              zIndex: 50,
            }
      }
      className="absolute"
      onMouseEnter={onMouseEnterPopout}
      onMouseLeave={onClose}
    >
      {/* M3X.WORKSPACE-CLOSURE Session 2.9 PR 3 — brand-teal accent tab
          connecting the icon to the popout. Reads as a continuous flow
          instead of a floating menu. */}
      <span
        aria-hidden
        data-popout-connector
        style={{
          position: "absolute",
          insetInlineStart: "-2px",
          top: "20px",
          width: "4px",
          height: "8px",
          backgroundColor: "#00D3C2",
          borderEndStartRadius: "0",
          borderStartStartRadius: "0",
          borderEndEndRadius: "2px",
          borderStartEndRadius: "2px",
        }}
      />
      {/* Arrow indicator — small white triangle pointing back at the icon.
          S2.9 PR 3 — bumped 10→12px so it pokes slightly into the connector. */}
      <span
        aria-hidden
        data-popout-arrow
        style={{
          position: "absolute",
          insetInlineStart: "-6px",
          top: "13px",
          width: "12px",
          height: "12px",
          backgroundColor: "white",
          borderLeft: "1px solid #e2e8f0",
          borderBottom: "1px solid #e2e8f0",
          transform: "rotate(45deg)",
        }}
      />

      <div
        data-popout-title
        style={{
          fontSize: "10px",
          fontWeight: 700,
          color: "#94a3b8",
          textTransform: "uppercase",
          letterSpacing: "0.05em",
          padding: "4px 8px 8px",
        }}
      >
        {subDomainLabel} · {screenCount} {screenCount === 1 ? "screen" : "screens"}
      </div>

      <ul>
        {subDomain.screens.map((screen) => {
          const base = screen.route.split(":")[0].replace(/\/$/, "");
          const isActive =
            currentPath === base || currentPath.startsWith(base + "/");
          return (
            <li key={screen.id}>
              <Link
                to={screen.route}
                onClick={() => {
                  onScreenClick?.();
                  onClose();
                }}
                data-popout-screen={screen.id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  padding: "6px 10px",
                  fontSize: "11px",
                  borderRadius: "6px",
                  fontWeight: isActive ? 600 : 400,
                  color: isActive ? "#2260C9" : "#334155",
                  background: isActive
                    ? "linear-gradient(90deg, #eef2ff 0%, #ecfeff 100%)"
                    : undefined,
                }}
                onMouseEnter={(e) => {
                  if (!isActive) {
                    (e.currentTarget as HTMLElement).style.backgroundColor =
                      "#f1f5f9";
                    (e.currentTarget as HTMLElement).style.color = "#1e293b";
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isActive) {
                    (e.currentTarget as HTMLElement).style.backgroundColor = "";
                    (e.currentTarget as HTMLElement).style.color = "#334155";
                  }
                }}
              >
                <span className="flex-1 truncate">
                  {locale === "ar" ? screen.labelAr : screen.labelEn}
                </span>
              </Link>
            </li>
          );
        })}
      </ul>
    </div>
  );
}


export default Rail2Popout;
